import React, { useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useScreenSize } from "../../utils/media-query";
import BaseFormWithBreadCrumbs from "../../components/base-form/base-form";
import Form, { GroupItem, SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import { useDataSource } from "../../contexts/dataSource";
import { useAuth } from "../../contexts/auth";
import useAxiosPrivate, { axiosHrPrivate } from "../../api/useAxios";
import { getSelectBoxPropertiesIdName, getSelectBoxPropertiesIdText } from "../../utils/default-props";
import { DataGrid } from "devextreme-react/data-grid";
import { Column, Editing, Lookup } from "devextreme-react/data-grid";
import LoaderPage from "../../layouts/loader-page/loader-page";

interface TravelFormData {
    id?: string;
    businessTripPurposeId: string;
    description: string;
    workAdvance: number;
    employeeBusinessTripEmployees: any[];
}

function TravelRequestCard({ employeeId }) {
    const navigate = useNavigate(); // Eklendi - navigation için
    const params = useParams();
    const { user } = useAuth();
    const { dataSourceHR } = useDataSource();
    const axiosPrivate = useAxiosPrivate(axiosHrPrivate);
    const screenSize = useScreenSize();
    const formRef = useRef();

    const [formData, setFormData] = useState<TravelFormData>({
        businessTripPurposeId: "",
        description: "",
        workAdvance: 0,
        employeeBusinessTripEmployees: []
    });
    const [readOnly, setReadOnly] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [id, setId] = useState(params.id);
    const [dataLoading, setDataLoading] = useState(false);

    // Eklenen callback fonksiyonları
    const handleFormSubmitSuccess = useCallback(() => {
        navigate('/travel-request/list');
    }, [navigate]);

    const handleParticipantsChange = useCallback((e: any) => {
        let updatedData = e.component.option("dataSource");

        // Grid verilerini API formatına dönüştür
        updatedData = updatedData.map(item => ({
            ...item,
            description: item.description || "", // Description yoksa boş string ekle
            willBeBusinessTripInsurance: item.willBeBusinessTripInsurance === 1 ? true : false,
            isVaccinationRequirement: item.isVaccinationRequirement === 1 ? true : false,
            isPassportRequired: item.isPassportRequired === 1 ? true : false
        }));

        setFormData(prev => ({
            ...prev,
            employeeBusinessTripEmployees: updatedData
        }));
        setDirty(true);
    }, []);

    const dbEmployees = useMemo(
        () =>
            dataSourceHR({
                loadUrl: "/api/Employee/ComboList",
                loadMode: "raw",
            }),
        [dataSourceHR]
    );

    const dbTravelPurpose = useMemo(
        () => dataSourceHR({
            loadUrl: "/api/EmployeeTravelPurpose",
            loadMode: "raw",
        }),
        [dataSourceHR]
    );

     const dbLocation = useMemo(
         () => dataSourceHR({
             loadUrl: "/api/EmployeeTravelLocation",
             loadMode: "raw",
         }),
         [dataSourceHR]
     );

    const dbTransportation = useMemo(
        () => dataSourceHR({
            loadUrl: "/api/EmployeeTravelTransport",
            loadMode: "raw",
        }),
        [dataSourceHR]
    );

    const dbAccommodation = useMemo(
        () => dataSourceHR({
            loadUrl: "/api/EmployeeTravelAccommodation",
            loadMode: "raw",
        }),
        [dataSourceHR]
    );

    const handleFieldDataChanged = useCallback(() => {
        setDirty(true);
    }, []);

    const onAfterLoadData = (data) => {
        setFormData((prev) => ({
            ...prev,
            ...data
        }));
    };

    // Form gönderilmeden önce veriyi düzenle
    const onBeforeSubmit = useCallback((formData) => {
        const preparedData = {
            ...formData,
            employeeBusinessTripEmployees: formData.employeeBusinessTripEmployees.map(employee => ({
                ...employee,
                description: employee.description || "",
                willBeBusinessTripInsurance: Boolean(employee.willBeBusinessTripInsurance),
                isVaccinationRequirement: Boolean(employee.isVaccinationRequirement),
                isPassportRequired: Boolean(employee.isPassportRequired)
            }))
        };
        return preparedData;
    }, []);

    const yesNoItems = [
        { id: 0, text: "Hayır" },
        { id: 1, text: "Evet" }
    ];

    return (
        <LoaderPage loading={dataLoading}>
            <div className={"content-block"}>
                <div className={"dx-card responsive-paddings"}>
                    <h3 className="mb-4">Sanırım yolculuk var. Herkese bizden selam. Yolunuz açık olsun!</h3>

                    <BaseFormWithBreadCrumbs
                        formData={formData}
                        setFormData={setFormData}
                        formRef={formRef}
                        readOnly={readOnly}
                        setReadOnly={setReadOnly}
                        dirty={dirty}
                        setDirty={setDirty}
                        loadId={id}
                        loadUrl={"/api/EmployeeTravel"}
                        updateUrl={"/api/EmployeeTravel/FormData"}
                        onAfterLoadData={onAfterLoadData}
                        dataLoading={dataLoading}
                        setDataLoading={setDataLoading}
                        onSubmitSuccess={handleFormSubmitSuccess} // Eklendi
                        setId={setId} // Eklendi
                        onBeforeSubmit={onBeforeSubmit}
                    >
                        <Form
                            formData={formData}
                            readOnly={readOnly}
                            onFieldDataChanged={handleFieldDataChanged}
                            ref={formRef}
                            labelLocation={screenSize.isXSmall ? "top" : "left"} // Eklendi
                            width={screenSize.isXSmall ? "100%" : "auto"} // Eklendi
                        >
                            <GroupItem>
                                <SimpleItem
                                    dataField="businessTripPurposeId"
                                    editorType="dxSelectBox"
                                    editorOptions={{
                                        dataSource: dbTravelPurpose,
                                        ...getSelectBoxPropertiesIdName,
                                    }}
                                >
                                    <Label text="Seyahat Amacı" />
                                    <RequiredRule message="Seyahat amacı zorunludur" />
                                </SimpleItem>

                                <SimpleItem
                                    dataField="description"
                                    editorType="dxTextArea" // değiştirildi: TextBox -> TextArea
                                    editorOptions={{ height: 90 }} // Eklendi
                                >
                                    <Label text="Açıklama" />
                                </SimpleItem>

                                <SimpleItem
                                    dataField="workAdvance"
                                    editorType="dxNumberBox"
                                >
                                    <Label text="İş Avansı" />
                                </SimpleItem>
                            </GroupItem>

                            <GroupItem caption="Seyahat Planı">
                                <SimpleItem>
                                    <DataGrid
                                        dataSource={formData.employeeBusinessTripEmployees}
                                        showBorders={true}
                                        height={200}
                                        onRowUpdated={handleParticipantsChange}
                                        onRowRemoved={handleParticipantsChange}
                                        onRowInserted={handleParticipantsChange}
                                    >
                                        <Editing
                                            allowAdding={!readOnly}
                                            allowDeleting={!readOnly}
                                            allowUpdating={!readOnly}
                                            mode="row"
                                            useIcons={true}
                                        />
                                        <Column
                                            dataField="employeeId"
                                            caption="Kim seyahat edecek?"
                                        >
                                            <Lookup
                                                dataSource={dbEmployees}
                                                {...getSelectBoxPropertiesIdText}
                                            />
                                        </Column>
                                        <Column
                                            dataField="description"
                                            caption="Açıklama"
                                        />
                                        <Column
                                            dataField="startDate"
                                            caption="Başlangıç Tarihi"
                                            dataType="date"
                                        />
                                        <Column
                                            dataField="endDate"
                                            caption="Bitiş Tarihi"
                                            dataType="date"
                                        />
                                        <Column
                                            dataField="fromBusinessTripLocationId"
                                            caption="Nereden"
                                        >
                                            <Lookup
                                                dataSource={dbLocation}
                                                {...getSelectBoxPropertiesIdName}
                                            />
                                        </Column>
                                        <Column
                                            dataField="toBusinessTripLocationId"
                                            caption="Nereye"
                                        >
                                            <Lookup
                                                dataSource={dbLocation}
                                                {...getSelectBoxPropertiesIdName}
                                            />
                                        </Column>
                                        <Column
                                            dataField="businessTripTransportationId"
                                            caption="Ulaşım"
                                        >
                                            <Lookup
                                                dataSource={dbTransportation}
                                                {...getSelectBoxPropertiesIdName}
                                            />
                                        </Column>
                                        <Column
                                            dataField="businessTripAccommodationId"
                                            caption="Konaklama"
                                        >
                                            <Lookup
                                                dataSource={dbAccommodation}
                                                {...getSelectBoxPropertiesIdName}
                                            />
                                        </Column>
                                        <Column
                                            dataField="willBeBusinessTripInsurance"
                                            caption="Seyahat Sigortası?"
                                            dataType="boolean"
                                        />
                                        <Column
                                            dataField="isVaccinationRequirement"
                                            caption="Aşı Zorunluluğu?"
                                            dataType="boolean"
                                        />
                                        <Column
                                            dataField="isPassportRequired"
                                            caption="Pasaport"
                                            dataType="boolean"
                                        />
                                    </DataGrid>
                                </SimpleItem>
                            </GroupItem>
                        </Form>
                    </BaseFormWithBreadCrumbs>
                </div>
            </div>
        </LoaderPage>
    );
}

export default TravelRequestCard;